import React, {useEffect, useMemo, useState} from 'react';
import {List, Map, Set} from 'immutable';
import cn from 'classnames';

import {makeStyles} from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Input from '@material-ui/core/OutlinedInput';
import Text from '@material-ui/core/Typography';

import {CoachingKpi, formatKpiValue, KpiObservation, KpiTargetId} from '@store/@types/kpi';
import Skill from 'records/Skill';
import KpiObservationActions from '@store/kpi/KpiObservationActions';
import {UserId} from 'records/User';
import {EditSquareIcon} from '../../../$pages/$rep-profile/kpi/history/EditSquareIcon';
import CoachingPlan from 'records/CoachingPlan';
import partitionBy from 'utils/partitionBy';
import GenericLink from 'components/GenericLink';
import {Int} from '@store/@types/util';
import {parseCurrency} from 'utils/parseCurrency';
import {Moment} from 'moment/moment';

export interface CoachingKpiEditListProps {
  createObservation: typeof KpiObservationActions.create;
  fetchCoachingKpis: typeof KpiObservationActions.coachingKpis;
  repId: UserId;
  coachingKpisByUser: Map<UserId, List<CoachingKpi>>;
  skills: List<Skill>;
  inProgress: boolean;
  plan: CoachingPlan;
  reviewCompletedAt: Moment | undefined;
  editedKpis: Set<KpiTargetId>;
  setEditedKpis: (kpis: Set<KpiTargetId>) => void;
}

const observationToStr = (observation?: KpiObservation): string => {
  if (!observation) {
    return '';
  }
  return `${formatKpiValue(
    observation.numericType,
    observation.value
  )} on ${observation.created.format('M/D/YYYY')}`;
};

export const CoachingKpiEditList: React.FC<CoachingKpiEditListProps> = ({
  createObservation,
  fetchCoachingKpis,
  repId,
  coachingKpisByUser,
  skills,
  inProgress,
  plan,
  reviewCompletedAt,
  editedKpis,
  setEditedKpis,
}) => {
  const coachingKpis = useMemo(
    () => coachingKpisByUser.get(repId, List()),
    [coachingKpisByUser, repId]
  );
  useEffect(() => {
    fetchCoachingKpis.defer({
      userId: repId,
      startDate: plan.created.clone().subtract(2, 'weeks'),
      endDate: reviewCompletedAt,
    });
  }, [fetchCoachingKpis, repId, plan, reviewCompletedAt]);

  const [showMore, setShowMore] = useState(false);
  const [editId, setEditId] = useState<KpiTargetId>();
  const [observationValue, setObservationValue] = useState<string>('');

  const handleSaveObservation = async () => {
    const parsedVal = parseCurrency(observationValue);
    if (isNaN(parsedVal)) {
      return console.warn('User supplied invalid value', observationValue);
    }

    await createObservation({
      userId: repId,
      kpiId: editId!,
      value: parsedVal,
    });
    await fetchCoachingKpis.defer({
      userId: repId,
      startDate: plan.created.clone().subtract(2, 'weeks'),
      endDate: plan.completedAt,
    });
    setEditedKpis(editedKpis.add(editId!));
    setEditId(undefined);
  };

  const [displayKpis, hasSkillKpis] = useMemo(() => {
    const [skillKpis, unrelatedKpis] = partitionBy(coachingKpis, k => k.skillRelated);

    // Show all KPIs if we have nothing skill related
    if (skillKpis.isEmpty()) return [unrelatedKpis, false];

    // Show skill related first, then if they ask for more, show 3 more
    const kpis = showMore ? skillKpis.concat(unrelatedKpis.take(3)) : skillKpis;
    return [kpis, true];
  }, [coachingKpis, showMore]);
  const unrelatedCount = Math.max(coachingKpis.filter(k => !k.skillRelated).size, 3);

  const {
    alignRight,
    columnSpacing,
    wrapper,
    inactiveColor,
    inputStyle,
    liItem,
    gridEl,
    titleEl,
    ulList,
  } = useStyles();
  const textColor = inProgress ? undefined : inactiveColor;

  if (coachingKpis.isEmpty()) return null;

  return (
    <div className={wrapper}>
      {hasSkillKpis ? (
        <h5>
          Goals / KPI associated with skill{skills.size ? 's' : ''}:{' '}
          {skills.map(el => el.skill).join(', ')}
        </h5>
      ) : (
        <h5>Goals / KPI</h5>
      )}
      <div className={cn(gridEl, titleEl, textColor)}>
        <div />
        <div className={cn(textColor, columnSpacing)}>
          {editedKpis.isEmpty() ? <div /> : <Text>Previous update</Text>}
          <Text style={{marginLeft: inProgress ? 46 : 20}} className={alignRight}>
            Most recent update
          </Text>
          <div />
        </div>
      </div>
      <section>
        <ul className={ulList}>
          {displayKpis.map(({goal, kpiId, lastObservation, beforeLastObservation}) => (
            <li key={kpiId} className={cn(liItem, gridEl)}>
              <Text className={textColor}>{goal}</Text>
              <div className={cn(textColor, columnSpacing)}>
                {editedKpis.has(kpiId) ? (
                  <Text style={{alignSelf: 'center'}}>
                    {observationToStr(beforeLastObservation)}
                  </Text>
                ) : (
                  <div />
                )}
                {editId === kpiId ? (
                  <Input
                    autoFocus
                    type="numeric"
                    className={inputStyle}
                    onChange={e => setObservationValue(e.target.value)}
                  />
                ) : (
                  <Text className={alignRight}>{observationToStr(lastObservation)}</Text>
                )}
                {lastObservation && (
                  <EditControls
                    isEdit={editId === kpiId}
                    inProgress={inProgress}
                    onEdit={() => setEditId(kpiId)}
                    onSave={handleSaveObservation}
                    onCancel={() => setEditId(undefined)}
                  />
                )}
              </div>
            </li>
          ))}
        </ul>
      </section>
      <ShowMoreLink
        coachingKpis={coachingKpis}
        plan={plan}
        unrelatedCount={unrelatedCount}
        showMore={showMore}
        setShowMore={setShowMore}
      />
    </div>
  );
};

interface ShowMoreLinkProps {
  plan: CoachingPlan;
  setShowMore: (v: boolean) => void;
  showMore: boolean;
  unrelatedCount: Int;
  coachingKpis: List<CoachingKpi>;
}

function ShowMoreLink({
  plan,
  setShowMore,
  showMore,
  unrelatedCount,
  coachingKpis,
}: ShowMoreLinkProps) {
  const allUnrelated = coachingKpis.every(k => !k.skillRelated);
  if (allUnrelated || showMore || unrelatedCount === 0) {
    return null;
  }

  const planSkill = plan.getSkillNames('/');
  const phrase = unrelatedCount === 1 ? '1 additional KPI' : `${unrelatedCount} additional KPIs`;

  return (
    <GenericLink onClick={() => setShowMore(true)}>
      Show {phrase} (non {planSkill} related)
    </GenericLink>
  );
}

interface EditControlsProps {
  inProgress: boolean;
  isEdit: boolean;
  onSave: () => void;
  onEdit: () => void;
  onCancel: () => void;
}

function EditControls({inProgress, isEdit, onSave, onCancel, onEdit}: EditControlsProps) {
  const {iconBtn, btnBlock} = useStyles();

  if (!inProgress) return null;
  if (isEdit) {
    return (
      <div className={btnBlock}>
        <Button size="small" onClick={onSave}>
          Save
        </Button>
        <Button size="small" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <IconButton onClick={onEdit} className={iconBtn}>
      <EditSquareIcon />
    </IconButton>
  );
}

const useStyles = makeStyles(theme => ({
  alignRight: {
    textAlign: 'right',
    alignSelf: 'flex-end',
    width: 175,
    marginLeft: 20,
  },
  columnSpacing: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginRight: 16,
  },
  inactiveColor: {
    color: '#8D8D8D',
  },
  btnBlock: {
    alignItems: 'center',
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'space-around',
    '& > button': {
      color: '#2879FF',
    },
  },
  iconBtn: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    padding: 0,
    marginLeft: 10,
    marginBottom: -2,
  },
  inputStyle: {
    maxWidth: 144,
    marginLeft: 20,
  },
  gridEl: {
    alignItems: 'center',
    display: 'flex',
    position: 'relative',
    '& > *:first-child': {
      width: '40%',
    },
    '& > *:nth-child(2n)': {
      width: '60%',
    },
  },
  liItem: {
    padding: '2.333px 0',
    minHeight: theme.spacing(6),
    '& > *': {
      boxSizing: 'border-box',
    },
    '& > *:first-child': {
      padding: '0 16px',
    },
    '&:not(:last-child)': {
      borderBottom: '1px solid #CCCCCC',
    },
  },
  ulList: {
    listStyleType: 'none',
    margin: 0,
    padding: 0,
  },
  titleEl: {
    margin: '8px 0',
  },
  wrapper: {
    marginBottom: 16,
    '& > section': {
      marginBottom: 5,
    },
  },
}));
